import React, { useState, useEffect } from "react";
import { SunIcon, MoonIcon } from "@heroicons/react/24/solid";

const ThemeToggle: React.FC = () => {
  // Initialize with system preference
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Check if we have a saved preference in localStorage
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme) {
      return savedTheme === "dark";
    }
    // Otherwise, use system preference
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  // Effect to apply theme changes
  useEffect(() => {
    // Apply theme changes to HTML element
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if the user hasn't manually set a preference
      if (!localStorage.getItem("theme")) {
        setIsDarkMode(e.matches);
      }
    };

    // Add listener for changes in system preference
    mediaQuery.addEventListener("change", handleChange);

    // Clean up
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600 transition-all duration-300 hover:shadow-md relative overflow-hidden"
      aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
    >
      <div className="relative z-10">
        {isDarkMode ? (
          <SunIcon className="h-5 w-5 transform transition-transform duration-300 scale-100" />
        ) : (
          <MoonIcon className="h-5 w-5 transform transition-transform duration-300 scale-100" />
        )}
      </div>
      <div
        className={`absolute inset-0 transition-transform duration-500 rounded-full ${
          isDarkMode
            ? "bg-yellow-400 -translate-x-full"
            : "bg-indigo-600 translate-x-0"
        } opacity-0`}
      ></div>
    </button>
  );
};

export default ThemeToggle;
